import { Stack, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { EDITABLE_LIST_FIELD, EditableListDataGridLayout, EditableListFormShowTemplate } from 'framework/list';
import { FormattedTextField, TextField } from 'framework/ui/field';
import { Button, Tooltip } from 'framework/ui/mui-builder';
import { DATE_FORMAT, EMPTY_VALUE_INDICATOR, useDateTimeService } from 'framework/utils';
import {
  ACCESS_INFO_ACTIONS,
  ACCESS_INFO_SECTIONS,
  useNewProjectUrl,
  useProjectSecurityContext,
} from 'domain/shared/newProject';
import { EmployeeLinkFieldWithNameLoading } from 'domain/shared/employee';

const CLIENT_PORTAL_REDMINE_REQUESTS_SOURCE = 'clientPortalRedmineRequests';

const ClientInvitationRequestsList = () => {
  const { formatDateWithTimezone } = useDateTimeService();
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const { getProjectClientPortalApproveInviteUrl } = useNewProjectUrl();
  const { isAccessGranted } = useProjectSecurityContext();
  const canReview = isAccessGranted(ACCESS_INFO_SECTIONS.CLIENT_PORTAL, ACCESS_INFO_ACTIONS.REVIEW);

  return (
    <Stack pt={2} spacing={1} pb={4}>
      <Typography variant="h6" color="primary">
        {canReview ? `Active requests` : `Requests history`}
      </Typography>
      <EditableListFormShowTemplate listSource={CLIENT_PORTAL_REDMINE_REQUESTS_SOURCE}>
        <EditableListDataGridLayout
          disabledAddNewRow
          getRowEnabled={() => true}
          rowProps={() => ({
            sx: {
              alignItems: 'center',
              px: 0,
            },
          })}
          getTableCells={async () => [
            {
              type: EDITABLE_LIST_FIELD,
              source: 'comment',
              isEditor: false,
              component: FormattedTextField,
              componentProps: {
                label: '',
                format: (comment: string) => comment || 'Client portal invitation request',
              },
              gridCellProps: { size: 6.3 },
            },
            {
              type: EDITABLE_LIST_FIELD,
              source: 'status',
              isEditor: false,
              component: TextField,
              componentProps: { label: '' },
              gridCellProps: { size: canReview ? 1.2 : 1.7 },
            },
            {
              type: EDITABLE_LIST_FIELD,
              source: 'submittedBy',
              isEditor: false,
              component: FormattedTextField,
              componentProps: {
                label: '',
                format: (submittedAt: string) =>
                  submittedAt ? (
                    <Tooltip title={'Submitted By'} placement={'left'}>
                      <div>
                        <EmployeeLinkFieldWithNameLoading source="submittedBy" label="" />
                      </div>
                    </Tooltip>
                  ) : (
                    EMPTY_VALUE_INDICATOR
                  ),
              },
              gridCellProps: { size: canReview ? 1.2 : 2 },
            },
            {
              type: EDITABLE_LIST_FIELD,
              source: 'submittedAt',
              isEditor: false,
              component: FormattedTextField,
              componentProps: {
                label: '',
                format: (submittedAt: string) =>
                  submittedAt ? (
                    <Tooltip title={'Submitted At'} placement={'left'}>
                      <div>{formatDateWithTimezone(submittedAt, DATE_FORMAT.FULL)}</div>
                    </Tooltip>
                  ) : (
                    EMPTY_VALUE_INDICATOR
                  ),
              },
              gridCellProps: { size: canReview ? 1.8 : 2, display: 'flex', justifyContent: 'flex-end' },
            },
            ...(canReview
              ? [
                  {
                    type: EDITABLE_LIST_FIELD,
                    source: 'id',
                    isEditor: false,
                    component: FormattedTextField,
                    componentProps: {
                      label: '',
                      format: (requestId: string) => (
                        <Button
                          variant="text"
                          size="small"
                          onClick={() =>
                            navigate(getProjectClientPortalApproveInviteUrl(projectId, requestId), {
                              preventScrollReset: true,
                            })
                          }
                        >
                          Review
                        </Button>
                      ),
                    },
                    gridCellProps: { size: 1.5, display: 'flex', justifyContent: 'flex-end' },
                  },
                ]
              : []),
          ]}
        />
      </EditableListFormShowTemplate>
    </Stack>
  );
};

export default ClientInvitationRequestsList;
